/**
 * Chiffrement de bout en bout des données applicatives.
 *
 * ## Pourquoi chiffrer alors que WebRTC chiffre déjà ?
 *
 * Le canal de données WebRTC est protégé par DTLS : sur un chemin P2P, ce module est
 * redondant. Mais le relais WebSocket (Phase 4) traverse le serveur **en clair**.
 * Appliquer la même couche à tous les chemins dès maintenant évite d'avoir à reprendre
 * le modèle de sécurité plus tard, et fait de la clé dans le fragment d'URL un mécanisme
 * réellement porteur plutôt qu'un argument marketing.
 *
 * ## Le dérivé d'IV est déterministe, pas aléatoire
 *
 * Chaque trame porte un numéro de séquence **global** (contrôle et données confondus).
 * L'IV en est dérivé par une injection bijective. Deux raisons :
 *
 * - **Aucune réutilisation d'IV possible.** Avec des IV aléatoires, la borne de
 *   l'anniversaire rend la collision improbable mais pas impossible ; une collision
 *   d'IV sous AES-GCM casse totalement la confidentialité. Ici c'est structurellement
 *   impossible tant que le compteur n'a pas bouclé (2^32 trames, soit ~256 To).
 * - **Pas de génération aléatoire par trame.** Une source d'aléa système sollicitée
 *   des millions de fois est un point de contention inutile.
 *
 * La clé est importée **non extractible** : même du code injecté dans la page ne peut
 * pas la relire depuis l'objet `CryptoKey`.
 */

import { IV_BYTES, TAG_BYTES, type Bytes } from '@attrape/shared';

/** Longueur du tag d'authentification GCM, en bits. */
const TAG_LENGTH_BITS = TAG_BYTES * 8;

/**
 * Importe une clé brute de 256 bits en clé AES-GCM non extractible.
 */
export async function importAesKey(raw: Bytes): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/**
 * Dérive l'IV d'une trame à partir de son numéro de séquence.
 *
 * L'IV fait 12 octets ; les 4 derniers portent le compteur en big-endian, le reste est nul.
 * C'est la construction recommandée pour GCM : un compteur explicite plutôt qu'un aléa.
 */
export function ivForSeq(seq: number): Bytes {
  const iv = new Uint8Array(IV_BYTES);
  new DataView(iv.buffer).setUint32(IV_BYTES - 4, seq >>> 0, false);
  return iv;
}

/**
 * Scelle un bloc de données.
 *
 * `aad` (données authentifiées additionnelles) lie le chiffré à sa position dans le flux :
 * une trame déplacée ne s'authentifie plus.
 */
export async function sealBytes(
  key: CryptoKey,
  plaintext: Bytes,
  iv: Bytes,
  aad: Bytes,
): Promise<Bytes> {
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: aad, tagLength: TAG_LENGTH_BITS },
    key,
    plaintext,
  );
  return new Uint8Array(ciphertext);
}

/**
 * Ouvre un bloc scellé.
 *
 * Lève si le tag d'authentification ne correspond pas — c'est-à-dire si les données ont
 * été altérées, ou si l'AAD ne correspond pas à la position attendue (trame rejouée ou
 * déplacée). L'appelant ne doit **jamais** ignorer cette exception : ce serait accepter
 * des données non authentifiées.
 */
export async function openBytes(
  key: CryptoKey,
  iv: Bytes,
  ciphertext: Bytes,
  aad: Bytes,
): Promise<Bytes> {
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, additionalData: aad, tagLength: TAG_LENGTH_BITS },
    key,
    ciphertext,
  );
  return new Uint8Array(plaintext);
}

/**
 * Compteur de trames d'une session.
 *
 * Un seul compteur par session, partagé entre trames de contrôle et trames de données :
 * c'est ce qui garantit l'unicité des IV, tous types confondus.
 */
export class FrameCounter {
  private next = 0;

  /** Consomme et renvoie le prochain numéro de séquence. */
  take(): number {
    const seq = this.next;
    this.next += 1;
    if (this.next > 0xffffffff) {
      throw new Error('Compteur de trames épuisé (2^32 trames) — session anormalement longue');
    }
    return seq;
  }

  /** Numéro qui sera renvoyé par le prochain `take()`. Utilisé pour les vérifications. */
  peek(): number {
    return this.next;
  }

  /** Nombre de trames émises depuis la création du compteur. */
  get emitted(): number {
    return this.next;
  }
}
